import React, { useMemo, useState } from "react";
import { Sparkles, Home, Users, HardHat, GraduationCap, Store, Handshake } from "lucide-react";
import ShareImpactButton from "@/components/ShareImpactButton";

const money = (v, cur = "GBP") =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: (cur || "GBP").toUpperCase(), maximumFractionDigits: 0 }).format(v || 0);

/**
 * Social Impact Calculator.
 *
 * NOTE (compliance): Financial return figures were removed on 5 August 2026
 * to align with our /about + /legal-status positioning that Mavula Investment
 * Ltd does NOT offer investment products, yields, or guaranteed returns.
 * This tool now shows social-impact projections only.
 *
 * Illustrative model — kept intentionally conservative.
 * Ratios (per official MAVULA one-pager):
 *   £10,000 → 3–5 sustainable local jobs (use mid-point 4)
 *   £25,000 → 1 family land plot
 *   ~450–620 jobs per 100 units built (use 535)
 */
export default function ImpactCalculator() {
  const [amount, setAmount] = useState(100000);
  const [cur, setCur] = useState("GBP");

  const model = useMemo(() => {
    const a = parseFloat(amount) || 0;
    const homesBuilt      = Math.max(0, Math.round(a / 100000));
    const familiesHoused  = homesBuilt * 1;
    const familyPlots     = Math.max(0, Math.round(a / 25000));
    const jobsSupported   = Math.max(0, Math.round((a / 10000) * 4));
    const trainingPlaces  = Math.max(0, Math.round(a / 50000));
    const localSuppliers  = Math.max(0, Math.round(a / 35000));
    const localWages      = a * 0.65;
    const youthWomen      = Math.round(jobsSupported * 0.6);
    return {
      homesBuilt, familiesHoused, familyPlots,
      jobsSupported, trainingPlaces, localSuppliers, localWages, youthWomen,
    };
  }, [amount]);

  return (
    <section className="py-14 md:py-20 border-t mv-hairline" data-testid="impact-calculator">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        <div className="mv-eyebrow"><span style={{ color: "var(--mv-gold)" }}>§ Social Impact Modeller</span></div>
        <h2 className="font-display text-3xl md:text-5xl mv-green-text mt-3 leading-[1.05]">
          Your money on the ground. <span className="font-accent italic" style={{ color: "var(--mv-gold)" }}>Doing real work.</span>
        </h2>
        <p className="mt-4 max-w-2xl text-[15px]" style={{ color: "var(--mv-muted)" }}>
          An illustrative view of the community impact your contribution can help fund — homes, jobs, training and local wages. This is <strong>not</strong> a projection of financial returns; Mavula Investment Ltd does not offer investment products.
        </p>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Inputs */}
          <div className="lg:col-span-2 mv-card p-6">
            <label className="block">
              <span className="text-[11.5px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>Contribution</span>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="font-display text-3xl mv-green-text">{money(amount, cur)}</span>
              </div>
              <input type="range" min="10000" max="1000000" step="5000" value={amount}
                     onChange={(e) => setAmount(parseFloat(e.target.value))}
                     className="mt-3 w-full accent-[color:var(--mv-green)]"
                     data-testid="impact-amount" />
              <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
                     className="mt-3 w-full px-3 py-2 rounded-xl border mv-hairline text-[14px]"/>
            </label>

            <label className="block mt-5">
              <span className="text-[11.5px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>Currency</span>
              <div className="mt-2 flex flex-wrap gap-2">
                {["GBP","USD","EUR","ZAR"].map((c) => (
                  <button key={c} onClick={() => setCur(c)}
                    className={`px-3 py-1 rounded-full text-[12.5px] font-medium border transition-colors ${cur === c ? "mv-green-bg text-white border-transparent" : "bg-white mv-hairline"}`}>
                    {c}
                  </button>
                ))}
              </div>
            </label>
          </div>

          {/* Outputs — social impact only */}
          <div className="lg:col-span-3 grid grid-cols-2 md:grid-cols-3 gap-3">
            <ImpactCard i={<Home size={20}/>} n={model.homesBuilt}       l="freehold homes built"/>
            <ImpactCard i={<Users size={20}/>} n={model.familiesHoused}   l="families housed"/>
            <ImpactCard i={<Handshake size={20}/>} n={model.familyPlots}   l="family land plots (£25k each)"/>
            <ImpactCard i={<HardHat size={20}/>} n={model.jobsSupported}  l="local jobs supported"/>
            <ImpactCard i={<Users size={20}/>} n={model.youthWomen}       l="reserved for youth & women (60%)"/>
            <ImpactCard i={<GraduationCap size={20}/>} n={model.trainingPlaces} l="Pula Learn training places"/>
            <ImpactCard i={<Store size={20}/>} n={model.localSuppliers}   l="local suppliers contracted"/>
            <ImpactCard i={<Sparkles size={20}/>} n={money(model.localWages, cur)} l="into local wages"/>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap justify-between items-center gap-3">
          <div className="text-[12px] font-accent italic" style={{ color: "var(--mv-muted)" }}>
            Illustrative social impact only — actual outcomes vary by country and project. Not a financial forecast, offer, or promise of returns.
          </div>
          <ShareImpactButton
            amount={parseFloat(amount) || 0}
            currency={cur}
            term={0}
            homes={model.homesBuilt}
            jobs={model.jobsSupported}
            training={model.trainingPlaces}
            wages={model.localWages}
          />
        </div>
      </div>
    </section>
  );
}

function ImpactCard({ i, n, l }) {
  return (
    <div className="mv-card p-4">
      <div className="w-9 h-9 rounded-2xl flex items-center justify-center mb-2 mv-green-text" style={{ background: "rgba(212,175,55,0.16)" }}>{i}</div>
      <div className="font-display text-xl mv-green-text">{n}</div>
      <div className="text-[11.5px] mt-0.5" style={{ color: "var(--mv-muted)" }}>{l}</div>
    </div>
  );
}
